import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useSiteNav } from "../hooks/useSiteContent";

const DEFAULT_NAV = [
  { label: "Home", target: "/", external: false },
  { label: "Services", target: "/services", external: false },
  { label: "About", target: "/about", external: false },
  { label: "Contact", target: "/contact", external: false },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dynamicNav = useSiteNav();
  const NAV = (dynamicNav && dynamicNav.length > 0 ? dynamicNav : DEFAULT_NAV).map((it) => ({
    label: it.label,
    to: it.target,
    external: it.external,
  }));
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full bg-white border-b transition-shadow ${
        scrolled ? "shadow-sm border-gray-200" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-6 lg:px-10 py-4 sm:py-5">
        <Logo size="xl" />

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            item.external ? (
              <a
                key={`${item.label}-${item.to}`}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="font-display text-sm uppercase tracking-[0.28em] font-bold text-[#0A192F] hover:text-[#D4AF37] transition-colors"
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={`${item.label}-${item.to}`}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-sm uppercase tracking-[0.28em] font-bold transition-colors ${
                    isActive ? "text-[#D4AF37]" : "text-[#0A192F] hover:text-[#D4AF37]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            )
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="navbar-cta-quote"
          className="hidden lg:inline-flex items-center bg-[#0A192F] text-white px-7 py-3.5 font-display text-xs uppercase tracking-[0.28em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
        >
          Request Quote
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#0A192F] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-200" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-3 font-display text-sm uppercase tracking-[0.25em] font-semibold min-h-[44px] flex items-center ${
                    isActive ? "text-[#D4AF37]" : "text-[#0A192F]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="mt-3 inline-flex justify-center items-center min-h-[48px] bg-[#0A192F] text-white px-6 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold"
            >
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
